import { getTelegramContext } from '../hooks/useTelegramContext';

/**
 * Thin `fetch` wrapper. Its only real job is attaching the auth header that the
 * resolved surface implies:
 *
 *   telegram -> `X-Telegram-Init-Data`   (HMAC key `HMAC_SHA256("WebAppData", token)`)
 *   browser  -> `X-Telegram-Login-Data`  (HMAC key `SHA256(token)`)
 *
 * Both are verified server-side by `bot/api/auth.py` (PR5). The client never
 * decides who the user is — it only forwards the proof it was handed.
 *
 * `VITE_USE_MOCKS=true` puts MSW in front of all of this as a dev fixture; the
 * request path is otherwise identical, mocked or not.
 */

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get isAuth() {
    return this.status === 401 || this.status === 403;
  }

  /** Signed in, but not allowed to write this — a viewer hitting a mutation. */
  get isForbidden() {
    return this.status === 403;
  }

  /**
   * No such list *for you*. `service.py` deliberately cannot distinguish "gone"
   * from "never yours" — a list you have no role on must be invisible, not
   * merely refused — so 404 covers both and the UI must not name the resource.
   */
  get isMissing() {
    return this.status === 404;
  }

  /** The request itself was answered; only transport failures are worth a retry. */
  static isTransportFailure(error: unknown): boolean {
    return !(error instanceof ApiError);
  }
}

/**
 * A 403 or 404 is a final answer about access, not a hiccup: no retry, no
 * skeleton, no "Retry" button. Screens branch on this to render a real message
 * instead of spinning forever — the failure mode the plan calls out explicitly.
 */
export function isAccessError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.isForbidden || error.isMissing);
}

export function authHeaders(): Record<string, string> {
  const ctx = getTelegramContext();
  if (ctx.mode === 'telegram') return { 'X-Telegram-Init-Data': ctx.initData };
  if (ctx.mode === 'browser') return { 'X-Telegram-Login-Data': ctx.authData };
  return {};
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON when present. */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
      ...(headers as Record<string, string> | undefined),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : null) ?? `Request failed (${response.status})`;
    throw new ApiError(response.status, message, parsed);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
